import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthActions, useConvexAuth } from "@convex-dev/auth/react";
import { ShieldAlert, Lock, Mail, ArrowRight, AlertCircle, Eye, EyeOff } from "lucide-react";
import { parseFriendlyError } from "@/lib/errorHandler";
import { getClientIp, getClientBrowserInfo } from "@/lib/auditHelper";
import { useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { signIn } = useAuthActions();
  const { isAuthenticated } = useConvexAuth();
  const recordAudit = useMutation(api.admin.recordAuditLog);

  const [email, setEmail] = useState("redboxart.gorontalo@gmail.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Redirect jika sudah authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate("/superadmin");
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const recordLoginAudit = async (
      action: string,
      target: string,
      details: string
    ) => {
      const [ip, ua] = await Promise.all([getClientIp(), getClientBrowserInfo()]);
      await recordAudit({
        actorEmail: email,
        action,
        category: "auth",
        target,
        details,
        ipAddress: ip,
        userAgent: ua,
      }).catch(() => {});
    };

    try {
      // signIn dulu. Auto-signUp hanya untuk email yang ada di server-side allowlist
      // (ditentukan oleh admin yang membuat akun via dashboard).
      try {
        await signIn("password", {
          email,
          password,
          flow: "signIn",
        });
      } catch (signInErr) {
        // Hanya izinkan auto-signUp untuk superadmin email agar owner onboard otomatis.
        // Untuk email lain, tolak agar tidak ada privilege escalation via sign-up sembarang.
        if (email.toLowerCase() !== "redboxart.gorontalo@gmail.com") {
          throw signInErr;
        }
        await signIn("password", {
          email,
          password,
          flow: "signUp",
        });
      }

      localStorage.setItem("fotomomen_admin_email", email);
      await recordLoginAudit("login_success", "/superadmin", "Superadmin berhasil masuk");
      navigate("/superadmin");
    } catch (err: any) {
      console.error("Login error:", err);
      // TIDAK ADA fallback hardcoded lagi. Fallback kredensial dari environment variable
      // emergency-only, untuk skip Convex Auth saat outage.
      const emergencyToken = import.meta.env.VITE_EMERGENCY_ADMIN_TOKEN as string | undefined;
      if (
        email.toLowerCase() === "redboxart.gorontalo@gmail.com" &&
        emergencyToken &&
        password === emergencyToken
      ) {
        localStorage.setItem("fotomomen_admin_email", email);
        await recordLoginAudit("login_success_emergency", "/superadmin", "Login via emergency token");
        navigate("/superadmin");
      } else {
        setError(parseFriendlyError(err, "Gagal masuk. Periksa email dan password."));
        recordLoginAudit("login_failed", "/admin/login", "Percobaan login gagal: kredensial tidak valid").catch(() => {});
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col justify-center items-center px-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-indigo-600/10 border border-indigo-500/20 text-indigo-400 mb-4 shadow-inner">
            <ShieldAlert className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">
            Superadmin FotoMomen
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Area terbatas. Akses khusus pengelola platform.
          </p>
        </div>

        {/* Card */}
        <div className="bg-slate-800/80 border border-slate-700/80 rounded-2xl p-8 backdrop-blur shadow-2xl">
          {error && (
            <div className="mb-6 flex items-center space-x-2 bg-rose-500/10 border border-rose-500/20 text-rose-300 px-4 py-3 rounded-xl text-sm">
              <AlertCircle className="w-4 h-4 shrink-0 text-rose-400" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1.5">
                Email Superadmin
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-slate-900/50 border border-slate-700 text-white text-sm rounded-xl pl-10 pr-4 py-2.5 focus:outline-none focus:border-indigo-500 transition"
                  placeholder="admin@fotomomen.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1.5">
                Password
              </label>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-slate-900/50 border border-slate-700 text-white text-sm rounded-xl pl-10 pr-12 py-2.5 focus:outline-none focus:border-indigo-500 transition"
                  placeholder="••••••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-3 text-slate-400 hover:text-white transition"
                  aria-label={showPassword ? "Sembunyikan password" : "Tampilkan password"}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full mt-2 flex items-center justify-center space-x-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white py-2.5 rounded-xl font-semibold text-sm transition shadow-lg shadow-indigo-600/20"
            >
              <span>{loading ? "Memverifikasi..." : "Masuk ke Dashboard"}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>

        {/* Footer info */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate("/")}
            className="text-xs text-slate-500 hover:text-slate-400 transition"
          >
            ← Kembali ke Beranda Utama
          </button>
        </div>
      </div>
    </div>
  );
}
